import React, { useState } from "react";
import { TbFileText, TbChevronDown, TbDeviceDesktop, TbDeviceDesktopOff, TbCloud } from "react-icons/tb";
import { riskBandFor, bandStyleScore, categoryGrade } from "../../../components/report/shapeReport";
// Imported, not re-declared. A local copy of this constant had drifted from the real
// value ('pipsqueak_internal_report_preview'), so every preview wrote to a key the
// preview page never reads — it opened on "No report to preview", every time.
import { INTERNAL_REPORT_PREVIEW_KEY } from "../../../components/InternalReportPreview";
import { groupForCategory, MODULE_GROUPS } from "../../lib/modules";
import { Button, Chip, EmptyState } from "../../components";
import { useAssetCoverage } from "../../hooks/useAssetCoverage";
import { useTheme } from "../../../ThemeContext";
import "./Scanner.css";

// Scan results.
//
// Deliberately does NOT rebuild the report. v1 welds ~900 lines of HTML-string report
// generation into the scanner component; duplicating that here would fork it. Preview
// hands off to the existing /internal-report-preview route, which is what already
// renders the full report.
//
// Severity and score banding come from components/report/shapeReport — a leaf module
// with no UI of its own. That is the one v1 import allowed here, because reimplementing
// the banding would let the two interfaces disagree about what "high risk" means.



const ScanResults = ({ results, domain, organizationId }) => {
  const { isDarkMode } = useTheme();
  const { coverage } = useAssetCoverage(results, organizationId);
  const [open, setOpen] = useState(null);
  if (!results) return null;

  const band = riskBandFor(results, results.riskScore);
  const style = bandStyleScore(band);

  const preview = () => {
    try {
      sessionStorage.setItem(INTERNAL_REPORT_PREVIEW_KEY, JSON.stringify({
        // Coverage travels with the results rather than as a separate key: shapeReport
        // reads data.coverage, so the report renders the same managed-coverage section
        // the card above shows — one source, no chance of the two disagreeing.
        results: coverage?.summary?.total ? { ...results, coverage: coverage.summary } : results,
        provider: null,
        theme: isDarkMode ? "dark" : "light",
      }));
    } catch {
      return; // payload too large for sessionStorage
    }
    // No 'noopener' on purpose: sessionStorage is only copied into a tab opened via
    // window.open when it stays in the same browsing-context group, and noopener
    // breaks exactly that. Safe here because the target is our own route.
    window.open("/internal-report-preview", "_blank");
  };

  // The backend returns seven categories; the form offered four groups, so results are
  // presented under the same four headings rather than switching vocabulary halfway.
  const grouped = MODULE_GROUPS.map((g) => ({
    ...g,
    categories: (results.categories || []).filter((c) => groupForCategory(c.name) === g.key),
  })).filter((g) => g.categories.length);

  return (
    <div className="v2-results">
      <div className="v2-results-head">
        <div className="v2-score" style={{ background: style?.bg, color: style?.fg, borderColor: style?.fg }}>
          {results.riskScore ?? "—"}
        </div>
        <div>
          <div className="v2-section-title">Risk score / 100</div>
          <div className="v2-results-band">{band || "Unrated"}</div>
          {/* Prospects read the number the way they read a vulnerability count — lower
              looked better to them. The direction has to be stated next to the figure,
              not left to the band label to imply. */}
          <div className="v2-score-scale">Higher is better · 100 = no issues found</div>
        </div>
        <div className="v2-results-stats">
          <Stat label="Critical" value={results.criticalCount} tone="danger" />
          <Stat label="Checks passed" value={results.passedCount} tone="success" />
          <Stat label="Findings" value={results.totalFindings ?? results.issueCount} />
          <Stat label="Categories" value={(results.categories || []).length} />
        </div>
        {/* <Button onClick={preview} style={{ marginLeft: "auto" }}>
          <TbFileText size={15} /> Preview report
        </Button> */}
      </div>

      {results.publicIpDiscovery && (
        <div className="v2-hint" role="status">
          <strong>Organization IP discovery</strong>
          {results.publicIpDiscovery.addresses?.length > 0
            ? `: ${results.publicIpDiscovery.addresses.length} IPs matched to organization evidence.${results.publicIpDiscovery.status === 'partial' ? ' Discovery was incomplete; review the limitations below.' : ''}`
            : results.publicIpDiscovery.status === 'unavailable'
              ? ': Registration or discovery services were unavailable. Organization ownership could not be checked. Retry discovery or enter confirmed IPs manually.'
              : results.publicIpDiscovery.status === 'partial'
                ? ': Discovery was incomplete. No organization IPs were confirmed from the available evidence. Review the limitations below, retry discovery or enter confirmed IPs manually.'
                : ': No organization IPs could be confirmed. Enter known organization IPs manually.'}
          {results.publicIpDiscovery.status === 'partial' && (
            <ul aria-label="Discovery limitations">
              {[
                [(results.publicIpDiscovery.diagnostics || []).some((d) => ['dns_unavailable', 'registration_unavailable', 'inventory_unavailable', 'registry_search_unavailable'].includes(d.reason)), 'Some DNS, registration or inventory lookups failed.'],
                [(results.publicIpDiscovery.diagnostics || []).some((d) => ['target_limit', 'registry_search_truncated', 'registered_block_too_large'].includes(d.reason)), 'Discovery limits were reached; additional addresses or registered blocks may exist.'],
                [(results.publicIpDiscovery.diagnostics || []).some((d) => ['spf_range_sampled', 'spf_range_unsupported'].includes(d.reason)), 'SPF ranges were sampled or skipped. Mail authorization alone does not prove organization ownership.'],
              ].filter(([show]) => show).map(([, message]) => <li key={message}>{message}</li>)}
            </ul>
          )}
          {results.publicIpDiscovery.registeredBlocks?.length > 0 && (
            <ul aria-label="Registered organization blocks">
              {results.publicIpDiscovery.registeredBlocks.map((block) => (
                <li key={block.handle || block.cidr}>
                  <strong>{block.cidr || `${block.startAddress} - ${block.endAddress}`}</strong>
                  {` - ${block.addressCount} addresses registered to ${block.ownerName}`}
                </li>
              ))}
            </ul>
          )}
          {results.publicIpDiscovery.candidates?.length > 0 && (
            <details style={{ marginTop: '.375rem' }}>
              <summary>Review discovered addresses and attribution</summary>
              <ul>
                {results.publicIpDiscovery.candidates.map((entry) => (
                  <li key={entry.ip}>
                    <strong>{entry.ip}</strong> - {entry.included ? 'Included' : 'Excluded'}
                    {entry.ownerName ? ` (${entry.ownerName})` : ''}: {entry.reasonLabel}
                    {entry.spfRanges?.length > 0 && ` SPF range seed: ${entry.spfRanges.join(', ')}.`}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
      {results.publicIps?.length > 0 && (
        <div className="v2-hint" style={{ overflowWrap: "anywhere" }}>
          <strong>{results.publicIps.length} public IP {results.publicIps.length === 1 ? "target" : "targets"}</strong>
          {" · "}{results.publicIps.slice(0, 8).join(", ")}
          {results.publicIps.length > 8 && ` and ${results.publicIps.length - 8} more`}
          {results.ipScan?.status === "partial" && (
            <div role="status">Some IP checks could not finish. Review the unavailable findings and retry those checks.</div>
          )}
        </div>
      )}

      <CoverageCard coverage={coverage} />

      {grouped.length === 0 ? (
        <EmptyState title="No findings returned">The scan completed but produced no categories.</EmptyState>
      ) : (
        <div className="v2-acc-list">
          {grouped.map((g) => {
            const findings = g.categories.flatMap((c) => c.findings || []);
            const worst = g.categories.map((c) => categoryGrade(c)).find(Boolean);
            const isOpen = open === g.key;
            return (
              <div key={g.key} className="v2-acc">
                <button type="button" className="v2-acc-head" aria-expanded={isOpen}
                        onClick={() => setOpen(isOpen ? null : g.key)}>
                  <span className="v2-acc-name">{g.label}</span>
                  {worst && <Chip>{worst}</Chip>}
                  <span className="v2-acc-count">{findings.length} {findings.length === 1 ? "finding" : "findings"}</span>
                  <TbChevronDown size={15} style={{ marginLeft: "auto", transform: isOpen ? "none" : "rotate(-90deg)", transition: "transform .15s ease" }} />
                </button>
                {isOpen && (
                  <div className="v2-acc-body">
                    {findings.length === 0 ? (
                      <p className="v2-hint" style={{ margin: 0 }}>Nothing flagged in this area.</p>
                    ) : findings.map((f, i) => (
                      <div key={i} className="v2-finding">
                        <span className={`v2-finding-stripe sev-${(f.severity || "low").toLowerCase()}`} />
                        <div>
                          <div className="v2-finding-title">{f.title || f.name}</div>
                          {f.description && <div className="v2-finding-desc">{f.description}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Managed coverage.
//
// The external scan finds hosts; the RMM connectors know which devices are managed. The
// gap between the two is the finding an MSP can act on and sell against, and it is not
// visible anywhere else in the product — a host nobody manages looks identical to a
// managed one in every other part of this screen.
//
// Renders nothing at all unless there is something true to say. Two states are silent on
// purpose: a scan that discovered no hosts, and a tenant with no RMM connected. Showing
// "0% managed" for either would be a number about our configuration dressed up as a
// finding about their estate.
const CoverageCard = ({ coverage }) => {
  if (!coverage) return null;
  const { summary, reason } = coverage;
  if (reason === 'no-hosts-discovered') return null;
  if (reason === 'no-managed-assets') return null;
  if (!summary) return null;

  const {
    total = 0, managed = 0, unmanaged = 0, coverage: pct,
    unmanagedHosts = [], thirdPartyHosts = [],
  } = summary;
  const thirdParty = thirdPartyHosts.length;
  if (!total && !thirdParty) return null;

  // Everything found runs on somebody else's platform. That is worth saying — it tells the
  // MSP where the client's public footprint actually lives — but it is not a coverage gap,
  // so there is no percentage and no warning styling.
  if (!total) {
    return (
      <div className="v2-coverage v2-coverage--info">
        <div className="v2-coverage-head">
          <span className="v2-coverage-icon"><TbCloud size={16} /></span>
          <span className="v2-section-title">Managed coverage</span>
          <span className="v2-hint">
            {thirdParty} internet-facing {thirdParty === 1 ? "host" : "hosts"} found, all hosted by third-party providers
          </span>
        </div>
        <p className="v2-hint" style={{ margin: "0 0 .5rem" }}>
          Nothing self-hosted was found to compare against your managed devices. Systems on
          cloud and SaaS platforms are maintained by those providers, so they are not counted
          as gaps.
        </p>
        <ThirdPartyHosts hosts={thirdPartyHosts} />
      </div>
    );
  }

  const clean = unmanaged === 0;

  return (
    <div className={`v2-coverage${clean ? " on" : ""}`}>
      <div className="v2-coverage-head">
        <span className="v2-coverage-icon">
          {clean ? <TbDeviceDesktop size={16} /> : <TbDeviceDesktopOff size={16} />}
        </span>
        <span className="v2-section-title">Managed coverage</span>
        <span className="v2-coverage-score">{pct}%</span>
        <span className="v2-hint">
          {managed} of {total} self-hosted {total === 1 ? "host is" : "hosts are"} under management
        </span>
      </div>

      {clean ? (
        <p className="v2-hint" style={{ margin: "0 0 .5rem" }}>
          Every self-hosted system this scan found matches a device your RMM already manages.
        </p>
      ) : (
        <>
          <p className="v2-hint" style={{ margin: "0 0 .5rem" }}>
            {unmanaged} {unmanaged === 1 ? "host is" : "hosts are"} reachable from the internet,
            {" "}not on a hosting provider, and match no managed device. These are the ones to
            {" "}check — they may be patched, monitored or backed up by no one.
          </p>
          <div className="v2-coverage-hosts">
            {unmanagedHosts.slice(0, 12).map((h) => (
              <span key={h.host} className="v2-chip" title={h.network ? `Network: ${h.network}` : undefined}>
                {h.host}
                {h.ips?.length > 0 && <b>{h.ips[0]}</b>}
              </span>
            ))}
            {unmanagedHosts.length > 12 && (
              <span className="v2-hint">and {unmanagedHosts.length - 12} more</span>
            )}
          </div>
        </>
      )}

      {thirdParty > 0 && (
        <div style={{ marginTop: ".625rem" }}>
          <div className="v2-hint" style={{ marginBottom: ".3125rem" }}>
            Also found, hosted by third-party providers (not counted above):
          </div>
          <ThirdPartyHosts hosts={thirdPartyHosts} />
        </div>
      )}
    </div>
  );
};

// Where a client's cloud-hosted systems live. Named by provider so the MSP can see at a
// glance why each is left out of the percentage.
const ThirdPartyHosts = ({ hosts }) => (
  <div className="v2-coverage-hosts">
    {hosts.slice(0, 12).map((h) => (
      <span key={h.host} className="v2-chip v2-chip--muted">
        {h.host}
        {h.provider && <b>{h.provider}</b>}
      </span>
    ))}
    {hosts.length > 12 && <span className="v2-hint">and {hosts.length - 12} more</span>}
  </div>
);

const Stat = ({ label, value, tone }) => (
  <div className="v2-stat">
    <div className="v2-section-title">{label}</div>
    <div className={`v2-stat-value${tone ? ` ${tone}` : ""}`}>{value ?? "—"}</div>
  </div>
);

export default ScanResults;
